import { useEffect, useMemo, useState } from 'react';
import { countryAPI } from '../../services/api';
import { responseData } from '../../services/apiResponse';
import CountryCombobox from './CountryCombobox';
import { readPhoneCountry } from './phoneCountryStorage';
import { countryOptions, toE164 } from './phoneNumber';

export default function PhoneNumberInput({ disabled, onChange, onCountryChange, invalid, layout = 'balanced' }) {
	const [countries, setCountries] = useState([]);
	const [country, setCountry] = useState('');
	const [nationalNumber, setNationalNumber] = useState('');
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [savedCountry] = useState(() => readPhoneCountry());
	const options = useMemo(() => countryOptions(countries), [countries]);
	const selectedCountry = useMemo(
		() => options.find((option) => option.alpha2 === country) || null,
		[country, options],
	);
	const columnLayout = layout === 'phoneWide'
		? 'grid-cols-[minmax(9rem,0.75fr)_minmax(0,1.25fr)]'
		: 'grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]';

	useEffect(() => {
		let active = true;
		countryAPI.getCountries().then((response) => {
			if (!active) return;
			setCountries(responseData(response) || []);
			setLoadError(false);
		}).catch(() => {
			if (!active) return;
			setCountries([]);
			setLoadError(true);
		}).finally(() => {
			if (active) setLoading(false);
		});
		return () => { active = false; };
	}, []);

	useEffect(() => {
		if (country || !savedCountry) return;
		const restored = options.find((option) => (
			option.alpha2 === savedCountry.alpha2
			&& option.callingCode === savedCountry.callingCode
		));
		if (restored) setCountry(restored.alpha2);
	}, [country, options, savedCountry]);

	useEffect(() => {
		onChange(toE164(country, nationalNumber));
	}, [country, nationalNumber, onChange]);

	useEffect(() => {
		onCountryChange?.(selectedCountry ? {
			alpha2: selectedCountry.alpha2,
			callingCode: selectedCountry.callingCode,
		} : null);
	}, [onCountryChange, selectedCountry]);

	return (
		<div className={`grid ${columnLayout} gap-2`}>
			<CountryCombobox
				options={options}
				value={country}
				onChange={(option) => setCountry(option.alpha2)}
				disabled={disabled}
				invalid={invalid && !country}
				loading={loading}
				error={loadError}
			/>
			<label className="min-w-0">
				<span className="sr-only">Phone number</span>
				<input
					type="tel"
					inputMode="tel"
					autoComplete="tel-national"
					value={nationalNumber}
					onChange={(event) => setNationalNumber(event.target.value)}
					disabled={disabled}
					placeholder="Phone number"
					className={`input-field h-12 min-w-0 ${invalid && nationalNumber ? 'border-red-400' : ''}`}
				/>
			</label>
		</div>
	);
}
